import type { RequestContext } from "../context/request-context.js";
import type { TechnicalCorrectionRepository } from "../../infrastructure/database/memory/in-memory-technical-correction-repository.js";
import {
  CORRECTION_SECTIONS,
  TECHNICAL_CORRECTION_PRIORITY_LABELS,
  buildCorrectionAnchor,
  correctionNotificationMessage,
  highestPriority,
  sectionLabelOf,
  validateCorrectionItems,
  type TechnicalCorrectionActor,
  type TechnicalCorrectionItemInput,
  type TechnicalCorrectionPriority,
  type TechnicalCorrectionRequest
} from "../../domain/review/technical-correction.js";
import { TECHNICAL_DOCUMENT_TYPE_LABELS, type TechnicalDocumentType } from "../../domain/review/technical-document-review.js";
import { AppError, ErrorCode } from "../../shared/errors/app-error.js";
import type { AuditRecorder } from "../../audit/audit-recorder.js";
import { AuditAction } from "../../audit/audit-log.js";
import { notificationService } from "./notification-service.js";

const randomUUID = () => globalThis.crypto.randomUUID();

/** Quem é o Inspetor responsável pelo documento — resolvido pelo módulo, nunca por texto livre. */
export interface CorrectionRecipient {
  professionalId: string | null;
  userId: string | null;
  name: string;
}

export interface RequestCorrectionsInput {
  documentType: TechnicalDocumentType;
  documentId: string;
  documentNumber: string;
  revision: string;
  clientId: string;
  /** Rota da página do documento — base do deep link. Para recomendações de Checklist, a rota do Checklist da inspeção. */
  documentRoute: string;
  /** Rota alternativa por seção (ex.: recomendações do RI abrem no Checklist). */
  sectionRoutes?: Partial<Record<string, string>>;
  reviewId?: string | null;
  assignedTo: CorrectionRecipient;
  items: TechnicalCorrectionItemInput[];
}

/**
 * Correções por item: cada linha do modal vira uma pendência com destino
 * exato. O Inspetor responsável recebe UMA notificação por item, com deep
 * link (documento + seção + item); "MARCAR COMO CORRIGIDO" fecha o item. As
 * pendências abertas alimentam a prioridade da fila do Painel.
 */
export class TechnicalCorrectionService {
  constructor(
    private readonly repo: TechnicalCorrectionRepository,
    private readonly identityRepo?: any,
    private readonly audit?: AuditRecorder
  ) {}

  async listByDocument(context: RequestContext, documentType: TechnicalDocumentType, documentId: string): Promise<readonly TechnicalCorrectionRequest[]> {
    return this.repo.listByDocument(context, documentType, documentId);
  }

  async listPendingByDocument(context: RequestContext, documentType: TechnicalDocumentType, documentId: string): Promise<TechnicalCorrectionRequest[]> {
    return (await this.repo.listByDocument(context, documentType, documentId)).filter(c => c.status === "PENDENTE");
  }

  /** Todas as pendências abertas do contexto (aba CORREÇÕES PENDENTES). */
  async listPending(context: RequestContext): Promise<TechnicalCorrectionRequest[]> {
    return (await this.repo.listAll(context)).filter(c => c.status === "PENDENTE").sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  }

  async listAll(context: RequestContext): Promise<readonly TechnicalCorrectionRequest[]> {
    return this.repo.listAll(context);
  }

  /** Pendências abertas atribuídas a um Profissional (o que o Inspetor vê). */
  async listPendingFor(context: RequestContext, professionalId: string): Promise<TechnicalCorrectionRequest[]> {
    return (await this.listPending(context)).filter(c => c.assignedTo.professionalId === professionalId);
  }

  /** Maior prioridade em aberto por documento — para ordenar a fila. */
  async pendingPriorityByDocument(context: RequestContext): Promise<Map<string, { priority: TechnicalCorrectionPriority | null; count: number }>> {
    const map = new Map<string, { priority: TechnicalCorrectionPriority | null; count: number }>();
    for (const c of await this.listPending(context)) {
      const key = `${c.documentType}:${c.documentId}`;
      const current = map.get(key) || { priority: null, count: 0 };
      map.set(key, { priority: highestPriority([current.priority, c.priority]), count: current.count + 1 });
    }
    return map;
  }

  /**
   * Registra os itens do pedido. Validação: seção e observação obrigatórias
   * por item; sem itens não há pedido. Cada item gera auditoria e notificação
   * própria ao Inspetor responsável.
   */
  async requestCorrections(context: RequestContext, input: RequestCorrectionsInput): Promise<TechnicalCorrectionRequest[]> {
    const errors = validateCorrectionItems(input.items);
    if (errors.length > 0) throw new AppError(ErrorCode.Validation, "Invalid correction items", { safeMessage: errors.join(" ") });
    if (!input.assignedTo || (!input.assignedTo.professionalId && !input.assignedTo.userId)) {
      throw new AppError(ErrorCode.Validation, "Inspector not resolved", { safeMessage: "Não foi possível identificar o Inspetor responsável pelo relatório. A correção não pode ser enviada." });
    }
    const now = new Date().toISOString();
    const requestedBy = await this.actorOf(context);
    const existing = await this.repo.listByDocument(context, input.documentType, input.documentId);
    let seq = existing.length;
    const created: TechnicalCorrectionRequest[] = [];
    for (const item of input.items) {
      seq += 1;
      const sectionId = item.sectionId.trim();
      const itemId = (item.itemId || "").trim();
      const fieldId = (item.fieldId || "").trim();
      const priority: TechnicalCorrectionPriority = item.priority && item.priority in TECHNICAL_CORRECTION_PRIORITY_LABELS ? item.priority : "NORMAL";
      const route = input.sectionRoutes?.[sectionId] || input.documentRoute;
      const correction: TechnicalCorrectionRequest = {
        id: `corr-${randomUUID()}`,
        documentType: input.documentType,
        documentId: input.documentId,
        documentNumber: input.documentNumber,
        revision: input.revision,
        reviewId: input.reviewId ?? null,
        sequence: String(seq).padStart(2, "0"),
        sectionId,
        sectionLabel: item.sectionLabel?.trim() || sectionLabelOf(input.documentType, sectionId),
        itemId,
        itemLabel: item.itemLabel?.trim() || itemId,
        fieldId,
        anchor: buildCorrectionAnchor(route, { sectionId, itemId: itemId || null, fieldId: fieldId || null }),
        comment: item.comment.trim(),
        priority,
        requestedBy,
        assignedTo: { userId: input.assignedTo.userId || "", professionalId: input.assignedTo.professionalId, name: input.assignedTo.name },
        createdAt: now,
        status: "PENDENTE",
        resolvedAt: null,
        resolvedBy: null,
        resolutionComment: ""
      };
      await this.repo.save(context, correction);
      created.push(correction);
      await this.recordAudit(context, AuditAction.CorrectionRequested, correction, { comment: correction.comment, priority, section: sectionId, item: itemId, field: fieldId, anchor: correction.anchor });
      await this.notifyInspector(input, correction);
    }
    return created;
  }

  /** "MARCAR COMO CORRIGIDO" — pelo Inspetor responsável (ou quem tenha permissão sobre o documento). */
  async markResolved(context: RequestContext, id: string, resolutionComment = ""): Promise<TechnicalCorrectionRequest> {
    const correction = await this.repo.findById(context, id);
    if (!correction) throw new AppError(ErrorCode.NotFound, "Correction not found", { safeMessage: "Item de correção não encontrado." });
    if (correction.status !== "PENDENTE") return correction;
    const now = new Date().toISOString();
    const resolvedBy = await this.actorOf(context);
    const updated: TechnicalCorrectionRequest = { ...correction, status: "CORRIGIDA", resolvedAt: now, resolvedBy, resolutionComment: resolutionComment.trim() };
    await this.repo.save(context, updated);
    await this.recordAudit(context, AuditAction.CorrectionResolved, updated, { resolutionComment: updated.resolutionComment });
    // Quem pediu fica sabendo que o item foi corrigido.
    if (correction.requestedBy.userId && correction.requestedBy.userId !== "system") {
      try {
        await notificationService.createNotification({
          userId: correction.requestedBy.userId, type: "INFO", event: "CORRECTION_RESOLVED",
          title: "Correção marcada como corrigida",
          message: `${correction.documentNumber}: item ${correction.sequence} (${correction.sectionLabel}${correction.itemLabel ? ` · ${correction.itemLabel}` : ""}) foi marcado como corrigido por ${resolvedBy.name}.`,
          entityType: "Correction", entityId: correction.id, route: correction.reviewId ? `/engineering/review/${correction.reviewId}` : `/engineering/workspace/${correction.documentId}`,
          idempotencyKey: `CORR_RESOLVED_${correction.id}`
        });
      } catch (e) { console.warn("[CORREÇÃO] Falha ao notificar", e); }
    }
    return updated;
  }

  /** Cancela um item ainda pendente (ex.: PLH desistiu). */
  async cancel(context: RequestContext, id: string, reason = ""): Promise<TechnicalCorrectionRequest> {
    const correction = await this.repo.findById(context, id);
    if (!correction) throw new AppError(ErrorCode.NotFound, "Correction not found", { safeMessage: "Item de correção não encontrado." });
    if (correction.status !== "PENDENTE") return correction;
    const now = new Date().toISOString();
    const updated: TechnicalCorrectionRequest = { ...correction, status: "CANCELADA", resolvedAt: now, resolvedBy: await this.actorOf(context), resolutionComment: reason.trim() };
    await this.repo.save(context, updated);
    return updated;
  }

  /** Inspetor responsável a partir do Professional (usuário resolvido pelo cadastro). */
  async recipientForProfessional(professionalId: string | null | undefined, fallbackName = ""): Promise<CorrectionRecipient> {
    if (!professionalId) return { professionalId: null, userId: null, name: fallbackName };
    const professional = this.identityRepo?.getProfessional ? await this.identityRepo.getProfessional(professionalId).catch(() => null) : null;
    let userId: string | null = professional?.userId ? String(professional.userId) : null;
    if (!userId && this.identityRepo?.listUsers) {
      const users: any[] = await this.identityRepo.listUsers().catch(() => []);
      const user = users.find(u => String(u.professionalId || "") === String(professionalId));
      userId = user ? String(user.id) : null;
    }
    return { professionalId: String(professionalId), userId, name: professional?.name || fallbackName };
  }

  sectionsFor(documentType: TechnicalDocumentType) {
    return CORRECTION_SECTIONS[documentType] || [];
  }

  // ---------------------------------------------------------- HELPERS

  private async notifyInspector(input: RequestCorrectionsInput, correction: TechnicalCorrectionRequest): Promise<void> {
    const userId = input.assignedTo.userId;
    if (!userId) return;
    const noun = CORRECTION_SECTIONS[input.documentType]?.find(s => s.id === correction.sectionId)?.itemNoun || "Item";
    try {
      await notificationService.createNotification({
        userId, type: "WARNING", event: "CORRECTION_REQUESTED_BY_PLH",
        title: "CORREÇÃO SOLICITADA PELO PLH",
        message: correctionNotificationMessage(correction.documentNumber, correction.sectionLabel, noun, correction.itemLabel),
        entityType: "Correction", entityId: correction.id, clientId: input.clientId, route: correction.anchor,
        target: { documentType: correction.documentType, documentId: correction.documentId, revision: correction.revision, sectionId: correction.sectionId, itemId: correction.itemId || null, fieldId: correction.fieldId || null, anchor: correction.anchor, recipientProfessionalId: input.assignedTo.professionalId },
        idempotencyKey: `CORR_${correction.id}`
      });
    } catch (e) {
      console.warn("[CORREÇÃO] Falha ao notificar o Inspetor", e);
    }
  }

  private async actorOf(context: RequestContext): Promise<TechnicalCorrectionActor> {
    const userId = context.userId || "system";
    const user = this.identityRepo && context.userId ? await this.identityRepo.getUser(context.userId).catch(() => null) : null;
    const professional = this.identityRepo && user?.professionalId ? await this.identityRepo.getProfessional(user.professionalId).catch(() => null) : null;
    return { userId, professionalId: professional ? String(professional.id) : (context.professionalId ? String(context.professionalId) : null), name: professional?.name || user?.name || userId };
  }

  private async recordAudit(context: RequestContext, action: AuditAction, correction: TechnicalCorrectionRequest, metadata: Record<string, unknown>): Promise<void> {
    if (!this.audit) return;
    try {
      await this.audit.record(context, {
        action,
        entityType: `TECHNICAL_CORRECTION:${correction.documentType}`,
        entityId: correction.id,
        previousState: null,
        newState: { status: correction.status } as any,
        metadata: { documentType: correction.documentType, documentTypeLabel: TECHNICAL_DOCUMENT_TYPE_LABELS[correction.documentType], documentId: correction.documentId, documentNumber: correction.documentNumber, revision: correction.revision, sequence: correction.sequence, assignedTo: correction.assignedTo.professionalId, performedBy: context.userId || "system", at: new Date().toISOString(), ...metadata } as any
      });
    } catch (e) {
      console.warn("[CORREÇÃO] Falha ao registrar auditoria", e);
    }
  }
}
